import { Box, Chip, Stack, Typography } from '@mui/material';
import { IdentityProviderSummaryItemFragment } from '../__generated__/graphql';
import { formatDate } from '../Formatters';
import { secretState } from './clientSecret';
import { isContactStale, STALE_AFTER_HOURS } from './lastContact';

interface Props {
  summary: IdentityProviderSummaryItemFragment;
}

export function syncLabel(summary: Pick<IdentityProviderSummaryItemFragment, 'scimEnabled' | 'scimAuthoritative' | 'scimHasActiveToken'>) {
  if (!summary.scimEnabled) return 'Synkronisering av';
  if (!summary.scimHasActiveToken) return 'Synkronisering mangler token';
  return summary.scimAuthoritative ? 'Brukerkatalogen styrer' : 'Synkronisering på';
}

const Metric = ({ label, value, hint, hintColor }: { label: string; value: string; hint?: string; hintColor?: string }) => (
  <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, px: 1.5, py: 1.25, minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography sx={{ fontSize: 20, fontWeight: 500, lineHeight: 1.3 }}>{value}</Typography>
    {hint && (
      <Typography variant="caption" color={hintColor ?? 'text.secondary'} component="div">
        {hint}
      </Typography>
    )}
  </Box>
);

export default function SsoStatus({ summary }: Props) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1" sx={{ mr: 1 }}>
          {summary.companyName ?? `Lisens ${summary.bksID}`}
        </Typography>
        {summary.scimEnabled && (
          <Chip
            size="small"
            color={!summary.scimHasActiveToken ? (summary.scimAuthoritative ? 'error' : 'warning') : summary.scimAuthoritative ? 'success' : 'info'}
            variant="outlined"
            label={syncLabel(summary)}
          />
        )}
        {secretState(summary) === 'invalid' && <Chip size="small" color="error" label="Klienthemmeligheten virker ikke" />}
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5 }}>
        <Metric
          label="Brukere på lisensen"
          value={summary.isRestricted ? `${summary.seatsInUse} / ${summary.seatLimit}` : `${summary.seatsInUse}`}
          hint={
            !summary.isRestricted
              ? 'Ingen grense'
              : summary.hasRoomForNewUser
                ? `Kjøpt ${summary.seatsBought} + ${summary.extraUserPercentage} % ekstra`
                : 'Full — nye brukere avvises'
          }
          hintColor={summary.isRestricted && !summary.hasRoomForNewUser ? 'error.main' : undefined}
        />
        <Metric label="Begrenset til kjøpte seter" value={summary.isRestricted ? `Ja, +${summary.extraUserPercentage} %` : 'Nei'} />
        <Metric
          label="Synkronisert fra brukerkatalogen"
          value={summary.scimEnabled ? `${summary.scimActiveUsers} aktive` : '—'}
          hint={summary.scimEnabled && summary.scimInactiveUsers > 0 ? `${summary.scimInactiveUsers} deaktivert` : undefined}
        />
        <Metric
          label="Sist kontakt med brukerkatalogen"
          value={!summary.scimEnabled ? '—' : summary.scimLastContactAt ? formatDate(summary.scimLastContactAt, 'DD.MM HH:mm') : 'Ingen ennå'}
          hint={summary.scimEnabled && isContactStale(summary.scimLastContactAt) ? `Over ${STALE_AFTER_HOURS} timer siden` : undefined}
          hintColor="error.main"
        />
      </Box>
    </Box>
  );
}
